import type {
  PqMarketCatalogueEntry,
  PqMarketProgram,
  PqTestName,
} from '@/features/prequalification/localControl/types'

/**
 * The full set of run/stop callbacks the market accordions thread down to the
 * per-test and per-resource rows. The detail page owns the mutations,
 * invalidation and alerts; everything below it is presentational.
 *
 * `pendingKeys` holds every control currently awaiting a mutation, so each row
 * that triggered one can disable its own buttons. It is a set rather than a
 * single key because the actions are independent and overlap freely: queueing
 * test A and then test B leaves both in flight, and a single slot would let B's
 * completion re-enable A's buttons while A is still running. Keys are
 * namespaced: `market:FCR_N`, `test:RAMP_N_MAX`, or a `runId` /
 * `resource:<id>:<test>`.
 */
export type ResourceSetTestActions = {
  /** Run every in-market test across all resources, optionally deferred. */
  onRunMarket: (market: PqMarketCatalogueEntry, startAt: string | null) => void
  /** Cancel every active run in a market. */
  onStopMarket: (market: PqMarketCatalogueEntry) => void
  /** Run one test across all resources, optionally deferred. */
  onRunTest: (testName: PqTestName, startAt: string | null) => void
  /** Cancel every active run for one test. */
  onStopTest: (testName: PqTestName) => void
  /** Run a single (resource, test), optionally deferred. */
  onRunResource: (resourceId: string, testName: PqTestName, startAt: string | null) => void
  onStopResource: (runId: string) => void
  /** Download the combined CSV for one test across all resource set resources. */
  onDownloadTest: (testName: PqTestName) => void
  onDownloadResource: (runId: string) => void
  onClearResource: (resourceId: string) => void
  pendingKeys: ReadonlySet<string>
}

export const marketKey = (market: PqMarketProgram): string => `market:${market}`
export const testKey = (testName: PqTestName): string => `test:${testName}`
export const resourceKey = (resourceId: string, testName: PqTestName): string => `resource:${resourceId}:${testName}`
